import { AdminUserSearchResult, BillingPlan, PaymentDetails, Plan, PlanPackageCriterion } from 'fs-api-client';
import { createDefaultApi } from 'src/webportal/utils/createApi';
import { BillingPlanModel, BillingPlanState, TierProp } from './BillingPlanModel';
import { Providers } from 'portal-shared-lib';

export class BillingPlanViewModel {
    private billingPlanModel: BillingPlanModel;

    constructor(
        private user: AdminUserSearchResult,
        private onBillingPlanLoaded: (billingPlan: BillingPlan, forecast: any, paymentDetails: PaymentDetails) => void,
        private onBillingPlanLoadError: (err: Error) => void, // eslint-disable-line
        private onBillingPlanUpdated: (billingPlanState: BillingPlanState) => void,
        private onBillingPlanSaved: (billingPlan: BillingPlan) => void,
        private onBillingPlanSaveError: (err: Error) => void, // eslint-disable-line
    ) {
        this.onBillingPlanUpdated = this.onBillingPlanUpdated.bind(this);
        this.billingPlanModel = new BillingPlanModel(null as any, null as any);
    }

    async fetchBillingPlan(userId: string): Promise<void> {
        const api = await createDefaultApi();
        try {
            const billingPlan = (await api.getUserBillingPlan(userId));
            this.billingPlanModel = new BillingPlanModel(billingPlan.body, this.onBillingPlanUpdated);

            const forecast = (await api.getForecast(userId)).body;
            const paymentDetails = (await api.getUserPaymentDetails(userId)).body;

            this.onBillingPlanLoaded(this.billingPlanModel.getBillingPlan(), forecast, paymentDetails);
        } catch (reqErr) {
            this.onBillingPlanLoadError(reqErr.response);
        }
    }

    async revertBillingPlan() {
        try {
            const api = await createDefaultApi();
            const newBillingPlan = (await api.revertUserBillingPlan(
                this.user.id
            )).body;
            this.billingPlanModel.resetBillingPlan(newBillingPlan);
            this.onBillingPlanSaved(this.billingPlanModel.getBillingPlan());
        } catch (reqErr: any) {
            const err = new Error(reqErr.response.body.message);
            this.onBillingPlanSaveError(err);
        }
    }

    async applyChanges(): Promise<void> {
        const updates = this.billingPlanModel.getBillingUpdates();
        try {

            const api = await createDefaultApi();
            const newBillingPlan = (await api.updateUserBillingPlan(
                this.user.id,
                {
                    id: this.billingPlanModel.getBillingPlan().id,
                    planPackageUpdates: updates
            })).body;

            this.billingPlanModel.resetBillingPlan(newBillingPlan);
            this.onBillingPlanSaved(this.billingPlanModel.getBillingPlan());

            this.billingPlanModel.applyUpdates();
        } catch (reqErr: any) {
            const err = new Error(reqErr.response.body.message);
            this.onBillingPlanSaveError(err);
        }
    }

    undoChanges() {
        this.billingPlanModel.undoChanges();
    }

    createCheckPoint() {
        this.billingPlanModel.createCheckPoint();
    }

    restoreCheckPoint() {
        this.billingPlanModel.restoreCheckPoint();
    }

    removeCheckPoint() {
        this.billingPlanModel.removeCheckPoint();
    }

    onNewPlanPackageAdded(): void {
        this.billingPlanModel.addNewPlanPackage();
    }

    onPlanPackageRemoved(planPackId: string): void {
        this.billingPlanModel.removePlanPackage(planPackId);
    }

    onPlanRemoved(planPackId: string, dimension: Plan.DimensionEnum): void {
        this.billingPlanModel.removePlanFormPlanPackage(planPackId, dimension);
    }

    onPlanPackStorageOwnerChanged(planPackId: string, criterionIdx: number, storageOwner: string): void {
        this.billingPlanModel.changePlanPackStorageOwner(planPackId, criterionIdx, storageOwner);
    }

    onPlanPackStorageProviderChanged(planPackId: string, criterionIdx: number, storageProvider: string): void {
        this.billingPlanModel.changePlanPackStorageProvider(planPackId, criterionIdx, storageProvider);
    }

    onPlanPackSubscriptionTypeChanged(planPackId: string, criterionIdx: number, subscriptionType: string): void {
        this.billingPlanModel.changePlanPackSubscriptionType(planPackId, criterionIdx, subscriptionType);
    }

    onSubscriptionTypeAdded(planPackId: string, subscriptionType: PlanPackageCriterion.SubscriptionTypeEnum) {
        this.billingPlanModel.addCriterion(planPackId, subscriptionType);
    }

    onSubscriptionTypeRemoved(planPackId: string, criterionIdx: number) {
        this.billingPlanModel.removeCriterion(planPackId, criterionIdx);
    }

    onPlanTierModified(
        newValue: string,
        planPackId: string,
        planDimension: Plan.DimensionEnum,
        tierIdx: number,
        tierProp: TierProp
    ) {
        this.billingPlanModel.modifyPlanTier(newValue, planPackId, planDimension, tierIdx, tierProp);
    }

    onPlanDimensionChanged(
        oldDimension: Plan.DimensionEnum,
        newDimension: Plan.DimensionEnum,
        planPackId: string
    ): void {
        this.billingPlanModel.modifyPlanDimension(oldDimension, newDimension, planPackId);
    }

    onPlanTierAdded(planPackId: string, planDimension: Plan.DimensionEnum): void {
        this.billingPlanModel.addPlanTier(planPackId, planDimension);
    }

    onPlanTierRemoved(planPackId: string, planDimension: Plan.DimensionEnum, planTierIdx: number): void {
        this.billingPlanModel.removePlanTier(planPackId, planDimension, planTierIdx);
    }

    addProvider(planPackId: string): void {
        this.billingPlanModel.addProvider(planPackId);
    }

    removeProvider(planPackId: string, criterionIdx: number): void {
        this.billingPlanModel.removeProvider(planPackId, criterionIdx);
    }

    changeProvider(planPackId: string, criterionIdx: number, newProvider: Providers): void {
        this.billingPlanModel.changeProvider(planPackId, criterionIdx, newProvider);
    }
}



// WEBPACK FOOTER //
// ./src/backoffice/components/billing/BillingPlanViewModel.ts